import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Search,
  Users,
  Calendar,
  MapPin,
  TrendingUp,
  LayoutGrid,
  Menu,
  Bell,
  Plus,
  Zap,
  Leaf,
  ShoppingBag,
  Rocket,
  Landmark,
  BrainCircuit,
  Eye,
  BarChart3
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { startupsApi, dashboardApi } from '@/services/api'
import { useAuth } from '@/hooks/use-auth'
import { StartupSubmission, StartupFilters } from '@/types'
import { capitalizeFirst, formatINR, getStartupData } from '@/utils'

export function StartupListPage() {
  const { user } = useAuth()
  const [searchParams] = useSearchParams()
  // Client-side state
  const [search, setSearch] = useState('')
  const [industryFilter, setIndustryFilter] = useState<string>('all')
  const [stageFilter, setStageFilter] = useState<string>('all')
  const [page, setPage] = useState(1)
  const ITEMS_PER_PAGE = 50

  const isFounder = user?.role === 'founder'

  // Fetch Logic: Founders use startupsApi (My Startups), Investors use dashboardApi (Public Directory)
  const { data: rawData, isLoading } = useQuery({
    queryKey: ['startups-list', { isFounder, search }],
    queryFn: async () => {
      if (isFounder) {
        // Founders: Get their own startups
        const res = await startupsApi.list({ page: 1, per_page: 100 })
        return res.data || []
      } else {
        // Investors: Get public startups with search
        const res = await startupsApi.list({ page: 1, per_page: 100, search: search || undefined })
        return res.data || []
      }
    },
  })

  // Normalize Data
  const normalizedStartups = useMemo(() => {
    if (!rawData) return []

    return rawData.map((item: any) => {
      // If it's already a StartupSubmission (Founder view), use as is
      if (item.profile && item.id) {
        return item as StartupSubmission
      }

      // If it's from Emerging Ventures (Investor view), map to StartupSubmission shape
      // Emerging Venture: { id, name, sector, insight, stage, location, funding_seeking, logo_url, analysis_status, analysis_id }
      return {
        id: item.id,
        status: item.analysis_status || 'submitted', // Use analysis status or default
        submission_timestamp: new Date().toISOString(), // Fallback
        analysis_completed: item.analysis_status === 'completed',
        profile: {
          company_info: {
            name: item.name,
            logo_url: item.logo_url,
            industry: item.sector,
            description: item.insight, // Use insight as description
            location: item.location,
            employee_count: 'N/A', // Not in emerging ventures
            funding_seeking: item.funding_seeking,
            funding_stage: item.stage,
          }
        }
      } as StartupSubmission
    })
  }, [rawData])

  // Client-side Filter
  const filteredData = useMemo(() => {
    return normalizedStartups.filter(startup => {
      const { company_info } = getStartupData(startup)

      // For investors, search is handled server-side, so we skip client-side search check
      // For founders, we keep client-side search if needed, but assuming consistency we can rely on server too.
      // However, founder endpoint in this file calls list() without search param currently.
      // Let's keep client search for founder, skip for investor (or double check)

      let matchesSearch = true
      if (isFounder) {
         matchesSearch = !search ||
          company_info.name.toLowerCase().includes(search.toLowerCase()) ||
          company_info.description?.toLowerCase().includes(search.toLowerCase()) ||
          company_info.industry.toLowerCase().includes(search.toLowerCase())
      } else {
         // Investor search is server-side, but strictly strictly speaking,
         // if the user types faster than refetch, client filtering helps UI responsiveness.
         // But purely relying on server is safer to avoid "flicker" of old results.
         // Let's assume server results are what we want.
         matchesSearch = true
      }

      const matchesIndustry = industryFilter === 'all' ||
        company_info.industry?.toLowerCase() === industryFilter.toLowerCase()

      const matchesStage = stageFilter === 'all' ||
        company_info.funding_stage?.toLowerCase() === stageFilter.toLowerCase()

      return matchesSearch && matchesIndustry && matchesStage
    })
  }, [normalizedStartups, search, industryFilter, stageFilter, isFounder])

  // Pagination (Client-side)
  const paginatedData = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE
    return filteredData.slice(start, start + ITEMS_PER_PAGE)
  }, [filteredData, page])

  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE)


  // --- Helper Functions ---

  const getIndustryIcon = (industry: string) => {
    const i = industry?.toLowerCase() || ''
    if (i.includes('energy') || i.includes('clean') || i.includes('tech')) return <Zap className="w-5 h-5" />
    if (i.includes('health') || i.includes('bio') || i.includes('med')) return <BrainCircuit className="w-5 h-5" />
    if (i.includes('agri') || i.includes('food')) return <Leaf className="w-5 h-5" />
    if (i.includes('finance') || i.includes('fintech')) return <Landmark className="w-5 h-5" />
    if (i.includes('commerce') || i.includes('retail')) return <ShoppingBag className="w-5 h-5" />
    return <Rocket className="w-5 h-5" />
  }

  const getIndustryColor = (industry: string) => {
    const i = industry?.toLowerCase() || ''
    if (i.includes('energy') || i.includes('clean')) return 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
    if (i.includes('health')) return 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400'
    if (i.includes('agri')) return 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
    if (i.includes('finance')) return 'bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400'
    if (i.includes('commerce')) return 'bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400'
    return 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400'
  }

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'bg-emerald-500/10 text-emerald-500 border-emerald-200/20'
      case 'approved': return 'bg-emerald-500/10 text-emerald-500 border-emerald-200/20'
      case 'pending': return 'bg-yellow-500/10 text-yellow-500 border-yellow-200/20'
      case 'rejected': return 'bg-red-500/10 text-red-500 border-red-200/20'
      default: return 'bg-slate-500/10 text-slate-500 border-slate-200/20'
    }
  }

  return (
    <div className="flex flex-col min-h-screen bg-background-light dark:bg-background-dark font-sans text-slate-900 dark:text-slate-100 transition-colors duration-300">

      {/* MAIN CONTENT */}
      <main className="flex-1 overflow-y-auto bg-slate-50/50 dark:bg-background-dark">
        <div className="p-6 md:p-10 max-w-7xl mx-auto w-full space-y-8">

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-1">
                {isFounder ? 'My Startups' : 'Startup Directory'}
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {isFounder ? 'Manage your submitted ventures.' : 'Discover and analyze verified startups.'}
              </p>
            </div>

            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                <input
                  className="pl-9 pr-4 py-2.5 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none w-full sm:w-64 text-slate-900 dark:text-white placeholder-slate-400 transition-all shadow-sm"
                  placeholder="Search startups..."
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <Select
                value={industryFilter}
                onValueChange={setIndustryFilter}
              >
                <SelectTrigger className="w-full sm:w-[160px] h-auto py-2.5 rounded-xl border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm font-medium">
                  <SelectValue placeholder="Industry" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Industries</SelectItem>
                  <SelectItem value="fintech">FinTech</SelectItem>
                  <SelectItem value="healthtech">HealthTech</SelectItem>
                  <SelectItem value="edtech">EdTech</SelectItem>
                  <SelectItem value="enterprise_software">Enterprise</SelectItem>
                  <SelectItem value="consumer_apps">Consumer</SelectItem>
                  <SelectItem value="ai_ml">AI/ML</SelectItem>
                  <SelectItem value="energy">Energy</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={stageFilter}
                onValueChange={setStageFilter}
              >
                <SelectTrigger className="w-full sm:w-[140px] h-auto py-2.5 rounded-xl border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm font-medium">
                  <SelectValue placeholder="Stage" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stages</SelectItem>
                  <SelectItem value="pre_seed">Pre-Seed</SelectItem>
                  <SelectItem value="seed">Seed</SelectItem>
                  <SelectItem value="series_a">Series A</SelectItem>
                  <SelectItem value="series_b">Series B</SelectItem>
                  <SelectItem value="series_c">Series C</SelectItem>
                </SelectContent>
              </Select>

              {!isFounder && (
                <Link to="/startups/new">
                  <Button className="flex items-center gap-2 bg-logo-gradient hover:opacity-90 text-white px-4 py-2.5 rounded-xl text-sm font-bold transition-all shadow-md shadow-primary/20 border-none h-full min-h-[42px]">
                    <Plus className="w-4 h-4" />
                    <span className="hidden lg:inline">New Evaluation</span>
                  </Button>
                </Link>
              )}
            </div>
          </div>

          {/* Grid Content */}
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-48 bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 animate-pulse" />
              ))}
            </div>
          ) : paginatedData.length === 0 ? (
            <div className="text-center py-20 bg-white dark:bg-slate-800 rounded-xl border border-dashed border-slate-200 dark:border-slate-700">
              <Rocket className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-1">No startups found</h3>
              <p className="text-slate-500 dark:text-slate-400">Try adjusting your filters or search terms.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
              {paginatedData.map((startup: StartupSubmission) => {
                const { company_info } = getStartupData(startup)
                const industryColor = getIndustryColor(company_info.industry)

                return (
                  <div key={startup.id} className="group bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5 shadow-sm hover:shadow-lg hover:border-primary/20 transition-all duration-300 flex flex-col justify-between h-full relative overflow-hidden">
                    {/* Hover Gradient Overlay */}
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 opacity-0 group-hover:opacity-100 transition-opacity"></div>

                    <div>
                      {/* Header: Logo + Badge */}
                      <div className="flex justify-between items-start mb-4">
                        <div className={`w-14 h-14 rounded-xl ${industryColor} flex items-center justify-center shrink-0 shadow-sm overflow-hidden bg-white p-1`}>
                          {company_info.logo_url ? (
                            <img src={company_info.logo_url} alt={company_info.name} className="w-full h-full object-contain" />
                          ) : (
                            getIndustryIcon(company_info.industry)
                          )}
                        </div>
                        <Badge variant="outline" className={`border-none ${getStatusColor(startup.status)}`}>
                          {capitalizeFirst(startup.status)}
                        </Badge>
                      </div>

                      {/* Title & Desc */}
                      <div className="mb-4">
                        <h3 className="text-lg font-bold text-slate-900 dark:text-white group-hover:text-primary transition-colors mb-1 line-clamp-1">
                          {company_info.name}
                        </h3>
                        <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                          {capitalizeFirst((company_info.industry || 'Tech').replace('_', ' '))} • {capitalizeFirst((company_info.funding_stage || 'Seed').replace('_', ' '))}
                        </div>
                        <p className="text-sm text-slate-600 dark:text-slate-300 line-clamp-2 leading-relaxed h-11">
                          {company_info.description || 'No description provided.'}
                        </p>
                      </div>

                      {/* Metrics */}
                      <div className="grid grid-cols-2 gap-y-2 gap-x-4 mb-4 py-3 border-t border-b border-slate-100 dark:border-slate-700/50">
                        <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                          <MapPin className="w-3.5 h-3.5 text-slate-400" />
                          <span className="truncate max-w-[100px]">{company_info.location || 'Remote'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                          <Users className="w-3.5 h-3.5 text-slate-400" />
                          <span>{company_info.employee_count || 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                          <TrendingUp className="w-3.5 h-3.5 text-slate-400" />
                          <span>{formatINR(company_info.funding_seeking || 0)}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                          <Calendar className="w-3.5 h-3.5 text-slate-400" />
                          <span>{startup.submission_timestamp ? new Date(startup.submission_timestamp).toLocaleDateString() : 'Recent'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 mt-auto">
                      <Link to={`/startups/${startup.id}`} className="flex-1">
                        <Button variant="outline" className="w-full text-xs font-bold border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 h-9">
                          <Eye className="w-3.5 h-3.5 mr-2" />
                          Details
                        </Button>
                      </Link>
                      {startup.analysis_completed ? (
                        <Link to={`/startups/${startup.id}/analysis`} className="flex-1">
                          <Button className="w-full text-xs font-bold bg-logo-gradient text-white border-none shadow-sm h-9">
                            <BarChart3 className="w-3.5 h-3.5 mr-2" />
                            Analysis
                          </Button>
                        </Link>
                      ) : (
                        <Button disabled className="flex-1 w-full text-xs font-bold border-slate-100 dark:border-slate-700 text-slate-400 h-9 bg-slate-50 dark:bg-slate-800">
                          Pending
                        </Button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 pt-6 border-t border-slate-200 dark:border-slate-700">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="border-slate-200 dark:border-slate-700 font-medium"
              >
                Previous
              </Button>
              <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="border-slate-200 dark:border-slate-700 font-medium"
              >
                Next
              </Button>
            </div>
          )}

        </div>
      </main>
    </div>
  )
}
